import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import Select from "react-select";
import Swal from "sweetalert2";
import enteFunciones from "../function/ente.Funcion";
import senaFunciones from "../function/sena.Funciones";

const RegistrarMatriculas = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEdit = useMemo(() => Boolean(id), [id]);

  const [anioLectivo, setAnioLectivo] = useState("");
  const [fichaGrupo, setFichaGrupo] = useState("");
  const [grupos, setGrupos] = useState([]);
  const [grupoSeleccionado, setGrupoSeleccionado] = useState("");

  const [coddaneEnteconformador, setCoddaneEnteconformador] = useState("");
  const [entes, setEntes] = useState([]);

  const [detallesMatricula, setDetallesMatricula] = useState([]);

  const [loading, setLoading] = useState(false);
  const [verificandoIdx, setVerificandoIdx] = useState(null);

  const opcionesEntes = useMemo(
    () =>
      entes.map((ente) => ({
        value: String(ente.coddaneEnteconformador),
        label: ente.nombreEnteconformador,
      })),
    [entes]
  );

  const opcionesGrupos = useMemo(
    () =>
      grupos.map((grupo) => ({
        value: String(grupo.fichaGrupo),
        label: String(grupo.fichaGrupo),
      })),
    [grupos]
  );

  const cleanData = useCallback(() => {
    setAnioLectivo("");
    setFichaGrupo("");
    setGrupoSeleccionado("");
    setCoddaneEnteconformador("");
    setDetallesMatricula([]);
  }, []);

  // -------------------- Cargar matrícula (si edición) --------------------
  useEffect(() => {
    const fetchMatricula = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/matriculas/listid/${id}`);
        const data = response.data;

        if (data.ok && data.matricula) {
          const matricula = data.matricula;

          setFichaGrupo(String(matricula.fichaGrupo || ""));
          setGrupoSeleccionado(String(matricula.fichaGrupo || ""));
          setAnioLectivo(String(matricula.anioLectivo || ""));
          setCoddaneEnteconformador(String(matricula.coddaneEnteconformador || ""));
        } else {
          Swal.fire({
            icon: "warning",
            title: "Atención",
            text: "No se encontró la matrícula.",
          });
        }
      } catch (error) {
        console.error("Error al cargar matrícula por ID:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo cargar la matrícula con el ID proporcionado.",
        });
      } finally {
        setLoading(false);
      }
    };

    if (isEdit) fetchMatricula();
  }, [id, isEdit]);

  // -------------------- Cargar detalles (si edición) --------------------
  useEffect(() => {
    const fetchDetalles = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/detallematricula/listbyid/${id}`);
        const data = response.data;

        if (data.ok && data.detalles) {
          const detallesFormateados = data.detalles.map((detalle) => {
            const aprendiz = detalle.aprendizObjectId || {};
            const nombreCompleto = [
              aprendiz.nombre1Aprendiz,
              aprendiz.nombre2Aprendiz,
              aprendiz.apellido1Aprendiz,
              aprendiz.apellido2Aprendiz,
            ]
              .filter(Boolean)
              .join(" ")
              .trim()
              .replace(/\s+/g, " ");

            return {
              ...detalle,
              documentoAprendiz: detalle.documentoAprendiz ?? aprendiz.documentoAprendiz ?? "",
              nombreAprendiz: nombreCompleto,
              estadoMatricula: detalle.estadoMatricula || "",
              tipoMatricula: detalle.tipoMatricula || "",
            };
          });

          setDetallesMatricula(detallesFormateados);
        } else {
          setDetallesMatricula([]);
        }
      } catch (error) {
        console.error("Error al cargar detalles de matrícula:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudieron cargar los detalles de la matrícula.",
        });
      } finally {
        setLoading(false);
      }
    };

    if (isEdit) fetchDetalles();
  }, [id, isEdit]);

  // -------------------- Cargar grupos --------------------
  useEffect(() => {
    const cargarGrupos = async () => {
      try {
        const gruposData = await senaFunciones.obtenerGrupos();
        setGrupos(gruposData || []);
      } catch (e) {
        console.error("Error cargando grupos:", e);
      }
    };
    cargarGrupos();
  }, []);

  // -------------------- Cargar entes --------------------
  useEffect(() => {
    const cargarEntes = async () => {
      try {
        const enteData = await enteFunciones.obtenerEntesConformadores();
        setEntes(enteData || []);
      } catch (e) {
        console.error("Error cargando entes:", e);
      }
    };
    cargarEntes();
  }, []);

  // -------------------- Detalles handlers --------------------
  const handleChange = (index, field, value) => {
    setDetallesMatricula((prev) => {
      const nuevos = [...prev];
      nuevos[index] = { ...nuevos[index], [field]: value };
      return nuevos;
    });
  };

  const removeDetalle = (index) => {
    setDetallesMatricula((prev) => prev.filter((_, i) => i !== index));
  };

  const addDetalle = () => {
    setDetallesMatricula((prev) => [
      ...prev,
      { documentoAprendiz: "", nombreAprendiz: "", estadoMatricula: "", tipoMatricula: "" },
    ]);
  };

  // -------------------- Verificar aprendiz por documento --------------------
  const verificarDocumentoAprendiz = async (documento, index) => {
    const doc = String(documento || "").trim();
    if (!doc) return;

    try {
      setVerificandoIdx(index);
      const response = await axios.get(`/api/aprendices/verificar/${doc}`);

      if (response.data?.exists) {
        handleChange(index, "nombreAprendiz", response.data.nombreCompleto || "");
      } else {
        handleChange(index, "nombreAprendiz", "");
      }
    } catch (error) {
      console.error("Error al verificar el documento del aprendiz:", error);
      handleChange(index, "nombreAprendiz", "");
    } finally {
      setVerificandoIdx(null);
    }
  };

  const handleDocumentoAprendizChange = (e, index) => {
    handleChange(index, "documentoAprendiz", e.target.value);
    handleChange(index, "nombreAprendiz", "");
  };

  const handleDocumentoAprendizKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      verificarDocumentoAprendiz(e.target.value, index);
    }
  };

  const handleDocumentoAprendizBlur = (e, index) => {
    verificarDocumentoAprendiz(e.target.value, index);
  };

  // -------------------- Guardar / Actualizar --------------------
  const saveMatricula = async () => {
    if (!fichaGrupo || !anioLectivo || !coddaneEnteconformador || detallesMatricula.length === 0) {
      return Swal.fire({
        icon: "error",
        title: "Error",
        text: "Completa los campos y agrega al menos un aprendiz.",
      });
    }

    const payload = {
      fichaGrupo: Number(fichaGrupo),
      anioLectivo: Number(anioLectivo),
      coddaneEnteconformador: Number(coddaneEnteconformador),
      detallesMatricula: detallesMatricula.map((d) => ({
        documentoAprendiz: Number(d.documentoAprendiz),
        estadoMatricula: d.estadoMatricula,
        tipoMatricula: d.tipoMatricula,
        fichaGrupo: Number(fichaGrupo),
      })),
    };

    try {
      setLoading(true);

      const response = await axios.post("/api/matriculas/add", payload);

      await Swal.fire({
        icon: "success",
        title: "Matrícula registrada con éxito",
        text: response.data?.message || "",
        showConfirmButton: false,
        timer: 2000,
      });

      cleanData(); // ✅ al guardar
      navigate("/matricula/listarmatriculas");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al enviar los datos",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateMatricula = async () => {
    if (!anioLectivo || !coddaneEnteconformador || detallesMatricula.length === 0) {
      return Swal.fire({
        icon: "error",
        title: "Error",
        text: "Completa los campos y agrega al menos un aprendiz.",
      });
    }

    const payload = {
      fichaGrupo: Number(fichaGrupo),
      anioLectivo: Number(anioLectivo),
      coddaneEnteconformador: Number(coddaneEnteconformador),
      detallesMatricula: detallesMatricula.map((d) => ({
        documentoAprendiz: Number(d.documentoAprendiz),
        estadoMatricula: d.estadoMatricula,
        tipoMatricula: d.tipoMatricula,
        fichaGrupo: Number(fichaGrupo),
      })),
    };

    try {
      setLoading(true);

      const response = await axios.put(`/api/matriculas/update/${id}`, payload);

      await Swal.fire({
        icon: "success",
        title: "Matrícula actualizada con éxito",
        text: response.data?.message || "",
        showConfirmButton: false,
        timer: 2000,
      });

      cleanData(); // ✅ al actualizar
      navigate("/matricula/listarmatriculas");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al actualizar la matrícula",
      });
    } finally {
      setLoading(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();
    isEdit ? await updateMatricula() : await saveMatricula();
  };

  // -------------------- Select handlers --------------------
  const handleGruposChange = (opcion) => {
    const value = opcion?.value || "";
    setGrupoSeleccionado(value);
    setFichaGrupo(value);
  };

  const disabledAll = loading;

  // Years options
  const years = useMemo(() => {
    const current = new Date().getFullYear();
    const start = 2015;
    return Array.from({ length: current - start + 1 }, (_, i) => start + i);
  }, []);

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">{isEdit ? "Editar Matrícula" : "Registrar Matrícula"}</h5>
                <small className="text-secondary">
                  Selecciona año, grupo y ente. Luego gestiona los aprendices matriculados.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate("/matricula/listarmatriculas")}
                  disabled={disabledAll}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-left me-2"></i>
                  Volver
                </button>

                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={cleanData}
                  disabled={disabledAll}
                  title="Limpiar"
                >
                  <i className="fa-solid fa-eraser me-2"></i>
                  Limpiar
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            <form id="form-matricula" onSubmit={actions} className="needs-validation" noValidate>
              {/* Card: Datos generales */}
              <div className="border rounded-4 p-3 p-md-4 mb-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                  <div className="fw-semibold">Datos generales</div>
                  <span className="badge text-bg-light border">
                    {detallesMatricula.length} aprendiz{detallesMatricula.length === 1 ? "" : "es"}
                  </span>
                </div>

                <div className="row g-3 align-items-end">
                  {/* Año Lectivo */}
                  <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold">Año Lectivo</label>
                    <select
                      className="form-select"
                      value={anioLectivo}
                      onChange={(e) => setAnioLectivo(e.target.value)}
                      required
                      disabled={disabledAll}
                    >
                      <option value="">Seleccione…</option>
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Grupos */}
                  <div className="col-12 col-md-3">
                    <label className="form-label fw-semibold">Grupo (Ficha)</label>
                    <Select
                      options={opcionesGrupos}
                      value={grupoSeleccionado ? opcionesGrupos.find((op) => op.value === grupoSeleccionado) : null}
                      onChange={handleGruposChange}
                      placeholder="Seleccione un grupo…"
                      isSearchable
                      isClearable
                      className="form-control p-0"
                      classNamePrefix="react-select"
                      isDisabled={disabledAll}
                    />
                  
                  </div>

                  {/* Entes */}
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold">Ente Conformador</label>
                    <Select
                      options={opcionesEntes}
                      value={
                        coddaneEnteconformador
                          ? opcionesEntes.find((op) => op.value === String(coddaneEnteconformador))
                          : null
                      }
                      onChange={(opcion) => setCoddaneEnteconformador(opcion?.value || "")}
                      placeholder="Seleccione un ente…"
                      isSearchable
                      isClearable
                      className="form-control p-0"
                      classNamePrefix="react-select"
                      isDisabled={disabledAll}
                    />
                  </div>
                </div>
              </div>

              {/* Card: Gestión de matriculados */}
              <div className="border rounded-4 p-3 p-md-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <div>
                    <div className="fw-semibold">Gestión de Matriculados</div>
                    <div className="small text-secondary">
                      Ingresa el documento y valida automáticamente el nombre del aprendiz.
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={addDetalle}
                    disabled={disabledAll}
                    title="Agregar Aprendiz"
                  >
                    <i className="fa-solid fa-user-plus me-2"></i>
                    Agregar
                  </button>
                </div>

                {/* Encabezados (md+) */}
                <div className="row text-secondary small fw-semibold d-none d-md-flex mb-2">
                  <div className="col-md-3">Documento</div>
                  <div className="col-md-4">Nombre</div>
                  <div className="col-md-2">Estado</div>
                  <div className="col-md-2">Tipo</div>
                  <div className="col-md-1 text-end"> </div>
                </div>

                {detallesMatricula.length === 0 ? (
                  <div className="text-center text-secondary py-4 border rounded-4">
                    <div className="display-6 mb-2">👥</div>
                    <div className="fw-semibold">Aún no hay aprendices</div>
                    <div className="small mb-3">Haz clic en “Agregar” para comenzar.</div>
                    <button
                      type="button"
                      className="btn btn-dark"
                      onClick={addDetalle}
                      disabled={disabledAll}
                    >
                      <i className="fa-solid fa-user-plus me-2"></i>
                      Agregar Aprendiz
                    </button>
                  </div>
                ) : (
                  detallesMatricula.map((detalle, index) => (
                    <div key={index} className="row align-items-end gy-2 mb-3">
                      {/* Documento */}
                      <div className="col-12 col-md-3">
                        <label className="form-label d-md-none fw-semibold">Documento</label>
                        <div className="input-group">
                          <span className="input-group-text">
                            <i className="fa-regular fa-id-card"></i>
                          </span>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Documento"
                            value={detalle.documentoAprendiz || ""}
                            onChange={(e) => handleDocumentoAprendizChange(e, index)}
                            onKeyDown={(e) => handleDocumentoAprendizKeyDown(e, index)}
                            onBlur={(e) => handleDocumentoAprendizBlur(e, index)}
                            required
                            disabled={disabledAll}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => verificarDocumentoAprendiz(detalle.documentoAprendiz, index)}
                            disabled={disabledAll || !String(detalle.documentoAprendiz || "").trim()}
                            title="Verificar"
                          >
                            <i
                              className={`fa-solid ${
                                verificandoIdx === index ? "fa-spinner fa-spin" : "fa-magnifying-glass"
                              }`}
                            ></i>
                          </button>
                        </div>
                      </div>

                      {/* Nombre */}
                      <div className="col-12 col-md-4">
                        <label className="form-label d-md-none fw-semibold">Nombre</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Se completa automáticamente"
                          value={detalle.nombreAprendiz || ""}
                          readOnly
                          disabled
                        />
                      </div>

                      {/* Estado */}
                      <div className="col-12 col-md-2">
                        <label className="form-label d-md-none fw-semibold">Estado</label>
                        <select
                          className="form-select"
                          value={detalle.estadoMatricula || ""}
                          onChange={(e) => handleChange(index, "estadoMatricula", e.target.value)}
                          required
                          disabled={disabledAll}
                        >
                          <option value="">Seleccione…</option>
                          <option value="Matriculado">Matriculado</option>
                          <option value="Pendiente">Pendiente</option>
                        </select>
                      </div>

                      {/* Tipo */}
                      <div className="col-12 col-md-2">
                        <label className="form-label d-md-none fw-semibold">Tipo</label>
                        <select
                          className="form-select"
                          value={detalle.tipoMatricula || ""}
                          onChange={(e) => handleChange(index, "tipoMatricula", e.target.value)}
                          required
                          disabled={disabledAll}
                        >
                          <option value="">Seleccione…</option>
                          <option value="Ordinaria">Ordinaria</option>
                          <option value="Extraordinaria">Extraordinaria</option>
                        </select>
                      </div>

                      {/* Eliminar */}
                      <div className="col-12 col-md-1 text-end">
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeDetalle(index)}
                          disabled={disabledAll}
                          title="Eliminar"
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))
                )}

                {/* Footer acciones */}
                <div className="pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        if (location.pathname === "/matricula/registrar") {
                          cleanData();
                        } else {
                          navigate("/matricula/listarmatriculas");
                        }
                      }}
                      disabled={disabledAll}
                      title="Cancelar"
                    >
                      <i className="fa-solid fa-xmark me-2"></i>
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      form="form-matricula"
                      className="btn btn-dark"
                      disabled={disabledAll}
                      title={isEdit ? "Actualizar" : "Guardar"}
                    >
                      <i
                        className={`fa-solid ${
                          loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                        } me-2`}
                      ></i>
                      {isEdit ? "Actualizar" : "Guardar"}
                    </button>
                  </div>
                </div>
              </div>
            </form>

          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarMatriculas;
